import React, { useState, useEffect, useCallback } from 'react';
import { ReportSelect } from '../ReportSelect';
import DashboardReportService from '@services/DashboardReportService';
import '../ReportViewer.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustomField {
  CustomFieldId: number;
  Name: string;
}

interface CardType {
  ConfigValueID: number;
  Name: string;
}

interface ApiConfig {
  apiUrl: string;
  authToken: string;
  timeZone: string;
}

interface Dashboard {
  DashboardId: number;
  ReportId: number;
  ConfigValueId?: number;
  ProjectId?: number;
}

interface SaveRequest {
  ReportId: number;
  WidgetId: number;
  DashboardId: number;
  LayerId: number | string | undefined;
  DueDate: string | undefined;
  StartDate: string | undefined;
  DateDeliveryFieldId: number;
  EstimatedFieldId: number;
  RemainingFieldId: number;
  AddDynamicGroup: boolean;
  CustomTeamSize: string | number;
  CardType: string;
  ConfigValueID: number;
}

interface Props {
  dashboard: Dashboard;
  config?: ApiConfig;
  settings: Record<string, unknown>;
  widgetId: number;
  onCancel: () => void;
  onSave: (
    request: SaveRequest,
    layerId: number | string | undefined,
    request2: SaveRequest
  ) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LOADING_FIELD: CustomField = { CustomFieldId: 0, Name: 'Loading...' };
const ALL_CARD_TYPES: CardType = { ConfigValueID: 0, Name: 'All Card Types' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Flatten grouped arrays and keep the one with the lowest CustomFieldId per group. */
function flattenAndSort(data: CustomField[] | CustomField[][]): CustomField[]
{
  const result: CustomField[] = [];
  for (const item of data)
  {
    if (Array.isArray(item))
    {
      const sorted = [...item].sort((a, b) => a.CustomFieldId - b.CustomFieldId);
      result.push(sorted[0]!);
    }
  }
  return result;
}

/** Resolve best pre-selected field id, falling back to a name match then the first option. */
function resolveFieldId(fields: CustomField[], currentId: number, fallbackName: string): number
{
  if (fields.find(f => f.CustomFieldId === currentId)) return currentId;

  const byName = fields.find(f => f.Name === fallbackName);
  if (byName) return byName.CustomFieldId;

  return fields.length > 0 ? fields[0]!.CustomFieldId : currentId;
}

// ─── Component ────────────────────────────────────────────────────────────────

const DetailsSettings: React.FC<Props> = ({
  dashboard,
  config: _config,
  settings,
  widgetId,
  onCancel,
  onSave,
}) =>
{
  // ── State ──────────────────────────────────────────────────────────────────
  const [customFields, setCustomFields] = useState<CustomField[]>([LOADING_FIELD]);
  const [_dateFields, setDateFields] = useState<CustomField[]>([LOADING_FIELD]);
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);

  const [estimatedField, setEstimatedField] = useState<number>(
    parseInt(String(settings.EstimatedFieldId ?? 0)),
  );
  const [remainingField, setRemainingField] = useState<number>(
    parseInt(String(settings.RemainingFieldId ?? 0)),
  );
  const [dateEnd, setDateEnd] = useState<number>(
    parseInt(String(settings.DateDeliveryFieldId ?? 0)),
  );
  const [addDynamicGroup, setAddDynamicGroup] = useState<boolean>(
    settings.AddDynamicGroup !== undefined ? JSON.parse(String(settings.AddDynamicGroup)) : true,
  );
  const [customTeamSize, setCustomTeamSize] = useState<string>(
    settings.CustomTeamSize !== undefined ? String(settings.CustomTeamSize) : '0',
  );
  const [selectedCardType, setSelectedCardType] = useState<CardType>({
    ConfigValueID: settings.ConfigValueID ? parseInt(String(settings.ConfigValueID)) : 0,
    Name: settings.CardType ? String(settings.CardType) : 'All Card Types',
  });

  const [_saving, setSaving] = useState(false);

  // ── Load data fields ───────────────────────────────────────────────────────
  const loadDataFields = useCallback(() =>
  {
    const baseRequest = {
      LayerId: -1,
      configValueId: dashboard.ConfigValueId ?? 1,
    };

    // Custom fields (TypeId = 3)
    DashboardReportService.getCustomFieldsByLayerAndId({ ...baseRequest, TypeId: 3 })

      .then(((data: CustomField[] | CustomField[][]) =>
      {
        const fields = flattenAndSort(data);

        setEstimatedField(prev => resolveFieldId(fields, prev, 'Estimated'));
        setRemainingField(prev => resolveFieldId(fields, prev, 'Remaining'));
        setCustomFields(fields);

        // Load card types in the same callback (same as legacy)
        DashboardReportService.getMenuByConfigValuePerson(dashboard.ProjectId ?? 0)

          .then(((response: { ConfigValueID: number; Text: string }[]) =>
          {
            const filtered = response
              .filter(x => ![1, 9, 15].includes(x.ConfigValueID))
              .map(c => ({ ConfigValueID: c.ConfigValueID, Name: c.Text }));
            setCardTypes([ALL_CARD_TYPES, ...filtered]);
          }) as any)
          .catch(console.error);
      }) as any);

    // Date fields (TypeId = 6)
    DashboardReportService.getCustomFieldsByLayerAndId({ ...baseRequest, TypeId: 6 })

      .then(((data: CustomField[] | CustomField[][]) =>
      {
        const fields = flattenAndSort(data);

        setDateEnd(prev => resolveFieldId(fields, prev, 'Due date'));
        setDateFields(fields);
      }) as any);
  }, [dashboard.ConfigValueId, dashboard.ProjectId]);

  useEffect(() =>
  {
    loadDataFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCustomTeamSizeChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const cleaned = e.target.value.replace(/[^0-9.]|(\..*\.)/g, '');
    setCustomTeamSize(cleaned);
  };

  const handleSave = async () =>
  {
    setSaving(true);

    const teamSizeValue =
      customTeamSize.length > 0 && customTeamSize !== '.'
        ? parseFloat(customTeamSize).toFixed(1)
        : 0;

    const request: SaveRequest = {
      ReportId: dashboard.ReportId,
      WidgetId: widgetId,
      DashboardId: dashboard.DashboardId,
      LayerId: settings.LayerId as number | string | undefined,
      DueDate: settings.DueDate as string | undefined,
      StartDate: settings.StartDate as string | undefined,
      DateDeliveryFieldId: dateEnd,
      EstimatedFieldId: estimatedField,
      RemainingFieldId: remainingField,
      AddDynamicGroup: addDynamicGroup,
      CustomTeamSize: Math.trunc(Number(teamSizeValue)),
      CardType: selectedCardType.Name,
      ConfigValueID: selectedCardType.ConfigValueID,
    };

    await DashboardReportService.updateWidgetDetailsSettings(request);
    setSaving(false);
    onSave(request, settings.LayerId as number | string | undefined, request);
  };

  // ── Derived select values ──────────────────────────────────────────────────
  const selectedEstimatedObj = customFields.find(f => f.CustomFieldId === estimatedField) ?? null;
  const selectedRemainingObj = customFields.find(f => f.CustomFieldId === remainingField) ?? null;
  const selectedCardTypeObj =
    cardTypes.find(c => c.ConfigValueID === selectedCardType.ConfigValueID) ?? selectedCardType;

  // ── Shared react-select styles ─────────────────────────────────────────────
  const selectStyles = {
    container: (b: object) => ({ ...b, width: 150 }),
    control: (b: object) => ({ ...b, minHeight: 27, height: 27 }),
    menu: (b: object) => ({ ...b, width: 150 }),
  };

  const isDark = document.documentElement.classList.contains('dark');

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="widget-content">
      <h4
        className="widget-setting-heading type-A"
        style={{ color: isDark ? '#a9a093' : 'inherit' }}
      >
        Widget Settings
      </h4>
      <div className="widget-setting-section-Settings widget-setting-select">
        {/* Card Types */}
        <div className="widget-setting-rowSettings">
          <div className="widget-setting-label">
            <span>Card Types:</span>
          </div>
          <div className="widget-setting-value widget-setting-estimated">
            <ReportSelect
              autoFocus={false}
              options={cardTypes}
              onChange={v => setSelectedCardType(v ?? ALL_CARD_TYPES)}
              value={selectedCardTypeObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.ConfigValueID)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Estimate Field */}
        <div className="widget-setting-rowSettings">
          <div className="widget-setting-label">
            <span>Estimate Field:</span>
          </div>
          <div className="widget-setting-value widget-setting-estimated">
            <ReportSelect
              autoFocus={false}
              options={customFields}
              onChange={v => setEstimatedField(v ? v.CustomFieldId : 0)}
              value={selectedEstimatedObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.CustomFieldId)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Remaining Field */}
        <div className="widget-setting-rowSettings">
          <div className="widget-setting-label">
            <span>Remaining Field:</span>
          </div>
          <div className="widget-setting-value widget-setting-remaining">
            <ReportSelect
              autoFocus={false}
              options={customFields}
              onChange={v => setRemainingField(v ? v.CustomFieldId : 0)}
              value={selectedRemainingObj}
              isMulti={false}
              isSearchable
              openMenuOnFocus
              closeMenuOnSelect
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.CustomFieldId)}
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Include Dynamic Column */}
        <div className="widget-setting-rowSettings">
          <div className="widget-setting-label">
            <label htmlFor="CheckboxChangeAddDynamicGroup">Include Dynamic Column</label>
          </div>
          <div className="widget-setting-label">
            <span className="custom-checkbox task-card-checkbox">
              <input
                id="CheckboxChangeAddDynamicGroup"
                className="taskCardCheckbox"
                type="checkbox"
                checked={addDynamicGroup}
                onChange={() => setAddDynamicGroup(prev => !prev)}
              />
              <label htmlFor="CheckboxChangeAddDynamicGroup" className="conversationCheckbox" />
            </span>
          </div>
        </div>

        {/* Custom People Assigned */}
        <div className="widget-setting-rowSettings">
          <div className="widget-setting-label">
            <label htmlFor={`NumericCustomTeamSize${widgetId}`}>Custom People Assigned</label>
          </div>
          <div className="widget-setting-value">
            <input
              id={`NumericCustomTeamSize${widgetId}`}
              className="ReportView input"
              type="number"
              step="1"
              value={customTeamSize}
              onChange={handleCustomTeamSizeChange}
            />
          </div>
        </div>

        {/* Actions */}

        <div className="widget-setting-actions flex justify-end gap-2">
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
              borderRadius: 4,
              background: isDark ? '#3a3c3c' : '#fff',
              color: isDark ? '#a9a093' : '#555',
              cursor: 'pointer',
            }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: 'none',
              borderRadius: 4,
              background: '#62accb',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailsSettings;
